import { Link } from 'react-router-dom';
import { useState } from 'react';
import SEOHead from '../../components/feature/SEOHead';

const contactSchema = {
  '@context': 'https://schema.org',
  '@type': 'ContactPage',
  name: 'Contact True North Vacations',
  description: 'Get in touch with True North Vacations to list your waterfront property or book a luxury cottage in Port Severn, Ontario.',
  url: `${import.meta.env.VITE_SITE_URL || ''}/contact`,
  mainEntity: {
    '@type': 'LocalBusiness',
    name: 'True North Vacations',
    telephone: '+17055382000',
    email: 'info@truenorthvacations.com',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Port Severn',
      addressRegion: 'ON',
      addressCountry: 'CA',
    },
    openingHoursSpecification: [
      { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday','Tuesday','Wednesday','Thursday','Friday'], opens: '09:00', closes: '18:00' },
      { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Saturday'], opens: '10:00', closes: '16:00' },
    ],
  },
};

export default function ContactPage() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', propertyAddress: '',
    propertyType: 'waterfront-cottage', message: '', inquiryType: 'list-property'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => formBody.append(key, value));
      const response = await fetch('https://readdy.ai/api/form/d6m3llp4a3uojp5fgiog', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString()
      });
      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', propertyAddress: '', propertyType: 'waterfront-cottage', message: '', inquiryType: 'list-property' });
      } else { setSubmitStatus('error'); }
    } catch { setSubmitStatus('error'); }
    finally { setIsSubmitting(false); }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Contact True North Vacations | Port Severn Property Management"
        description="Contact True North Vacations in Port Severn, Ontario. List your waterfront property, book a luxury cottage, or get answers to your questions. Call (705) 538-2000 or email us today."
        keywords="contact True North Vacations, Port Severn property management contact, list waterfront cottage Ontario, vacation rental inquiry Port Severn"
        canonical={`${import.meta.env.VITE_SITE_URL || ''}/contact`}
        ogTitle="Contact True North Vacations | Port Severn Property Management"
        ogDescription="Get in touch with True North Vacations. List your waterfront property or book a luxury cottage in Port Severn, Ontario."
        schemaJson={contactSchema}
      />
      {/* Navigation */}
      <nav className="bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2">
              <i className="ri-compass-3-line text-2xl text-[#0A2540]"></i>
              <span className="text-base sm:text-xl font-serif font-semibold text-[#0A2540]">True North Vacations</span>
            </Link>
            <div className="hidden lg:flex items-center space-x-6 xl:space-x-8">
              <Link to="/listings" className="text-sm font-light text-gray-700 hover:text-[#C9A961] transition-colors whitespace-nowrap">Properties</Link>
              <Link to="/why-list" className="text-sm font-light text-gray-700 hover:text-[#C9A961] transition-colors whitespace-nowrap">For Owners</Link>
              <Link to="/services" className="text-sm font-light text-gray-700 hover:text-[#C9A961] transition-colors whitespace-nowrap">Services</Link>
              <Link to="/owners-portal" className="text-sm font-light text-gray-700 hover:text-[#C9A961] transition-colors whitespace-nowrap">Owner Portal</Link>
              <Link to="/contact" className="text-sm font-light text-[#C9A961] whitespace-nowrap">Contact</Link>
              <Link to="/contact" className="bg-[#C9A961] text-white px-5 py-2.5 rounded-full text-sm font-medium hover:bg-[#B8984E] transition-colors whitespace-nowrap">List Your Property</Link>
            </div>
            <button className="lg:hidden p-2 text-[#0A2540] cursor-pointer" onClick={() => setMenuOpen(!menuOpen)} aria-label="Toggle menu">
              <i className={`text-2xl ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
          {menuOpen && (
            <div className="lg:hidden pt-4 pb-4 border-t border-gray-100 mt-4 space-y-3">
              <Link to="/listings" onClick={() => setMenuOpen(false)} className="block text-sm text-gray-700 hover:text-[#C9A961] py-2">Properties</Link>
              <Link to="/why-list" onClick={() => setMenuOpen(false)} className="block text-sm text-gray-700 hover:text-[#C9A961] py-2">For Owners</Link>
              <Link to="/services" onClick={() => setMenuOpen(false)} className="block text-sm text-gray-700 hover:text-[#C9A961] py-2">Services</Link>
              <Link to="/owners-portal" onClick={() => setMenuOpen(false)} className="block text-sm text-gray-700 hover:text-[#C9A961] py-2">Owner Portal</Link>
              <Link to="/contact" onClick={() => setMenuOpen(false)} className="block text-sm text-[#C9A961] py-2">Contact</Link>
              <Link to="/contact" onClick={() => setMenuOpen(false)} className="inline-block bg-[#C9A961] text-white px-6 py-2.5 rounded-full text-sm font-medium hover:bg-[#B8984E] transition-colors mt-2">List Your Property</Link>
            </div>
          )}
        </div>
      </nav>

      {/* Hero */}
      <section className="relative h-[280px] sm:h-[400px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://public.readdy.ai/ai/img_res/c62c4bff703696fdcd16ee7cf3f9bfd3.jpg" alt="Contact Us" className="w-full h-full object-cover object-center" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>
        <div className="relative z-10 text-center px-4">
          <h1 className="text-3xl sm:text-5xl font-serif text-white mb-3 sm:mb-4">Get In Touch</h1>
          <p className="text-base sm:text-xl text-white/90 font-light">Let's discuss how we can help maximize your property's potential</p>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-12 sm:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
            {/* Form */}
            <div className="bg-white rounded-3xl shadow-xl p-6 sm:p-8 lg:p-12">
              <h2 className="text-2xl sm:text-3xl font-bold text-[#0A2540] mb-6">Send Us a Message</h2>
              {submitStatus === 'success' && (
                <div className="mb-6 bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm">Thank you! We'll get back to you within 24 hours.</div>
              )}
              {submitStatus === 'error' && (
                <div className="mb-6 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">Something went wrong. Please try again or call us directly.</div>
              )}
              <form onSubmit={handleSubmit} data-readdy-form id="contact_form">
                <div className="space-y-5">
                  <div>
                    <label htmlFor="inquiryType" className="block text-sm font-semibold text-[#0A2540] mb-2">I'm interested in</label>
                    <select id="inquiryType" name="inquiryType" value={formData.inquiryType} onChange={handleChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C9A961] focus:border-transparent text-sm">
                      <option value="list-property">Listing My Property</option>
                      <option value="booking">Booking a Property</option>
                      <option value="general">General Inquiry</option>
                      <option value="owner-support">Owner Support</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-[#0A2540] mb-2">Full Name *</label>
                    <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C9A961] focus:border-transparent text-sm" placeholder="John Smith" />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-[#0A2540] mb-2">Email Address *</label>
                    <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C9A961] focus:border-transparent text-sm" placeholder="john@example.com" />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold text-[#0A2540] mb-2">Phone Number</label>
                    <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C9A961] focus:border-transparent text-sm" placeholder="(705) 555-0123" />
                  </div>
                  <div>
                    <label htmlFor="propertyAddress" className="block text-sm font-semibold text-[#0A2540] mb-2">Property Address (if applicable)</label>
                    <input type="text" id="propertyAddress" name="propertyAddress" value={formData.propertyAddress} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C9A961] focus:border-transparent text-sm" placeholder="123 Waterfront Drive, Port Severn" />
                  </div>
                  <div>
                    <label htmlFor="propertyType" className="block text-sm font-semibold text-[#0A2540] mb-2">Property Type</label>
                    <select id="propertyType" name="propertyType" value={formData.propertyType} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C9A961] focus:border-transparent text-sm">
                      <option value="waterfront-cottage">Waterfront Cottage</option>
                      <option value="lakefront-estate">Lakefront Estate</option>
                      <option value="cabin">Cabin</option>
                      <option value="luxury-home">Luxury Home</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-semibold text-[#0A2540] mb-2">Message *</label>
                    <textarea id="message" name="message" value={formData.message} onChange={handleChange} required maxLength={500} rows={5} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C9A961] focus:border-transparent text-sm resize-none" placeholder="Tell us about your property or inquiry..." />
                    <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 characters</p>
                  </div>
                  <button type="submit" disabled={isSubmitting} className="w-full bg-[#0A2540] text-white px-8 py-4 rounded-full text-base font-medium hover:bg-[#0A2540]/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer">
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </button>
                </div>
              </form>
            </div>

            {/* Contact Info */}
            <div className="space-y-6 sm:space-y-8">
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold text-[#0A2540] mb-4 sm:mb-6">Contact Information</h2>
                <p className="text-gray-600 text-base leading-relaxed">We're here to answer your questions and help you get started.</p>
              </div>
              <div className="space-y-5">
                {[
                  { icon: 'ri-map-pin-line', title: 'Office Location', content: 'Port Severn, Ontario' },
                  { icon: 'ri-phone-line', title: 'Phone', content: '(705) 321-7933', href: 'tel:+17053217933' },
                  { icon: 'ri-mail-line', title: 'Email', content: 'info@truenorthvacations.com', href: 'mailto:info@truenorthvacations.com' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start space-x-4">
                    <div className="w-11 h-11 bg-[#C9A961]/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className={`${item.icon} text-xl text-[#C9A961]`}></i>
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-[#0A2540] mb-1">{item.title}</h3>
                      {item.href ? (
                        <a href={item.href} className="text-gray-600 hover:text-[#C9A961] transition-colors text-sm">{item.content}</a>
                      ) : (
                        <p className="text-gray-600 text-sm">{item.content}</p>
                      )}
                    </div>
                  </div>
                ))}
                <div className="flex items-start space-x-4">
                  <div className="w-11 h-11 bg-[#C9A961]/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-xl text-[#C9A961]"></i>
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-[#0A2540] mb-1">Business Hours</h3>
                    <p className="text-gray-600 text-sm">Mon – Fri: 9:00 AM – 6:00 PM</p>
                    <p className="text-gray-600 text-sm">Saturday: 10:00 AM – 4:00 PM</p>
                    <p className="text-gray-600 text-sm">Sunday: Closed</p>
                  </div>
                </div>
              </div>
              <div className="bg-[#0A2540] rounded-2xl p-6 sm:p-8">
                <h3 className="text-xl font-bold text-white mb-3">Quick Response Guarantee</h3>
                <p className="text-gray-300 leading-relaxed text-sm">We pride ourselves on responsive communication. Expect a reply within 24 hours during business days, often much sooner.</p>
              </div>
              <div className="bg-[#F5F1E8] rounded-2xl p-6 sm:p-8">
                <h3 className="text-lg font-bold text-[#0A2540] mb-4">Follow Us</h3>
                <div className="flex items-center space-x-4">
                  {['ri-facebook-fill', 'ri-instagram-line', 'ri-linkedin-fill', 'ri-mail-line'].map((icon, i) => (
                    <a key={i} href={i === 3 ? 'mailto:info@truenorthvacations.com' : '#'} className="w-11 h-11 bg-[#0A2540] rounded-full flex items-center justify-center hover:bg-[#C9A961] transition-colors">
                      <i className={`${icon} text-lg text-white`}></i>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="py-12 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0A2540] mb-4">Visit Port Severn</h2>
            <p className="text-gray-600 text-base sm:text-lg">Discover the beauty of Georgian Bay's waterfront paradise</p>
          </div>
          <div className="rounded-3xl overflow-hidden shadow-xl">
            <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d45449.89999999999!2d-79.7833!3d44.8167!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4d2ac5e5e5e5e5e5%3A0x5e5e5e5e5e5e5e5e!2sPort%20Severn%2C%20ON!5e0!3m2!1sen!2sca!4v1234567890123!5m2!1sen!2sca" width="100%" height="400" style={{ border: 0 }} allowFullScreen loading="lazy" title="Port Severn Location"></iframe>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0A2540] text-white py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-12 mb-10 sm:mb-12">
            <div className="col-span-2 lg:col-span-1">
              <div className="flex items-center space-x-3 mb-4">
                <i className="ri-compass-3-line text-2xl text-[#C9A961]"></i>
                <span className="text-lg font-serif font-semibold">True North Vacations</span>
              </div>
              <p className="text-gray-400 text-sm">Port Severn's Premier Luxury Vacation Rental Management</p>
            </div>
            <div>
              <h4 className="text-base font-semibold mb-4 text-[#C9A961]">Services</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><Link to="/services" className="hover:text-white transition-colors">Property Management</Link></li>
                <li><Link to="/services" className="hover:text-white transition-colors">Maintenance</Link></li>
                <li><Link to="/services" className="hover:text-white transition-colors">Guest Services</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-base font-semibold mb-4 text-[#C9A961]">For Owners</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><Link to="/contact" className="hover:text-white transition-colors">List Your Property</Link></li>
                <li><Link to="/owners-portal" className="hover:text-white transition-colors">Owner Portal</Link></li>
                <li><Link to="/why-list" className="hover:text-white transition-colors">Revenue Calculator</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-base font-semibold mb-4 text-[#C9A961]">Properties</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><Link to="/listings" className="hover:text-white transition-colors">Current Listings</Link></li>
                <li><Link to="/contact" className="hover:text-white transition-colors">Booking Inquiry</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-700 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-gray-400 text-sm">© 2025 True North Vacations. All rights reserved.</p>
            <div className="flex items-center space-x-5">
              <a href="#" className="text-[#C9A961] hover:text-white transition-colors"><i className="ri-facebook-fill text-xl"></i></a>
              <a href="#" className="text-[#C9A961] hover:text-white transition-colors"><i className="ri-instagram-line text-xl"></i></a>
              <a href="#" className="text-[#C9A961] hover:text-white transition-colors"><i className="ri-linkedin-fill text-xl"></i></a>
              <a href="mailto:info@truenorthvacations.com" className="text-[#C9A961] hover:text-white transition-colors"><i className="ri-mail-line text-xl"></i></a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
